import useImageRecovery from '../../../hooks/useImageRecovery';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useQuery, useLazyQuery } from '@apollo/client';
import useCheckValidity from '../../../util/useCheckValidity';
import { Image, Transformation } from 'cloudinary-react';
import environment from '../../../env-config';
import { FiDownload, FiExternalLink, FiMoreVertical, FiX } from 'react-icons/fi';
import { PRIMARY_COLOR_1, NOTE_BACKGROUND, PRIMARY_COLOR_8, SECONDARY_COLOR, WHITE } from '../../../appearance/Colors';
import { isCloudinaryMedia, isCloudinaryMediaUrl } from '../../../util/cloudinary';
import { REFRESH_MESSAGE_MEDIA_URLS } from '../../../util/imageRecovery';
import { GET_MESSAGE } from '../../../gql-operations/queries/message/get-message';
import QUERY from '../../../gql-operations/queries';
import { LAYOUT, isMobileViewport, calculateImageFrameBounds } from '../layoutBreakpoints';
import useDownloadFile from '../../../hooks/useDownloadFile';
import { downloadReadOnlyAttachment } from '../../../util/readOnlyAttachmentActions';
import { isTauriEnvironment, openExternalUrl } from '../../../utils/tauri';

const toStableUrlKey = value => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    try {
        const parsed = new URL(raw);
        return `${parsed.origin}${parsed.pathname}`;
    } catch (error) {
        return raw.split('?')[0].split('#')[0];
    }
};

const STABLE_IMAGE_URL_CACHE_LIMIT = 300;
const stableImageUrlCache = new Map();

const readCachedImageUrl = sourceKey => {
    const key = String(sourceKey || '').trim();
    if (!key) return '';
    return String(stableImageUrlCache.get(key) || '').trim();
};

const rememberCachedImageUrl = (sourceKey, imageUrl) => {
    const key = String(sourceKey || '').trim();
    const url = String(imageUrl || '').trim();
    if (!key || !url) return;

    if (stableImageUrlCache.has(key)) {
        stableImageUrlCache.delete(key);
    }
    stableImageUrlCache.set(key, url);

    if (stableImageUrlCache.size > STABLE_IMAGE_URL_CACHE_LIMIT) {
        const oldestKey = stableImageUrlCache.keys().next().value;
        if (oldestKey) {
            stableImageUrlCache.delete(oldestKey);
        }
    }
};

function ImageSection({ readOnly = false, url, width, image, crop, onClick, messageId, isUploading = false, uploadProgress = 0, uploadStatus = 'uploading', onCancelUpload, cover = false, ...rest }) {
    const {t: lnT} = useTranslation();
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0 });
    const actionButtonRef = useRef(null);
    const actionMenuRef = useRef(null);
    const downloadFile = useDownloadFile();
    const { refetch: refetchDownloadFile } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });

    // Just checking if the image exist from cloudinary
    // const originalSize = cloudinaryCore.url(metaData.image, { quality: 'auto', fetchFormat: 'auto' });
    // console.log('url', originalSize)
    // let valid = checkImageValidity(originalSize);
    // console.log('valid is', valid)

    // const imageWidth = width ? image && image.width < 100 ? image.width : width : 300;

    const isCompactViewport = isMobileViewport();
    const defaultMediaWidth = isCompactViewport ? LAYOUT.COMPACT_MEDIA_WIDTH : LAYOUT.DEFAULT_MEDIA_WIDTH;
    const resolvedImageWidth = width || defaultMediaWidth;
    const imageWidth = (image && image.width) < 100 ? 60 : resolvedImageWidth;

    // Calculate aspect ratio for placeholder
    // Prefer explicit height/width, then aspect_ratio, then calculate from dimensions
    const getAspectRatio = () => {
        // First try to calculate from width/height if both are valid
        if (image?.width && image?.height && 
            image.width > 0 && image.height > 0 && 
            !isNaN(image.width) && !isNaN(image.height) &&
            isFinite(image.width) && isFinite(image.height)) {
            const calculatedRatio = image.height / image.width;
            // Validate the calculated ratio is reasonable
            if (calculatedRatio > 0.1 && calculatedRatio < 10 && isFinite(calculatedRatio)) {
                return calculatedRatio;
            }
        }
        // Fallback to explicit aspect_ratio if available
        if (image?.aspect_ratio && 
            typeof image.aspect_ratio === 'number' && 
            !isNaN(image.aspect_ratio) && 
            isFinite(image.aspect_ratio) &&
            image.aspect_ratio > 0.1 && 
            image.aspect_ratio < 10) {
            return image.aspect_ratio;
        }
        return null; // No aspect ratio available
    };

    const aspectRatio = getAspectRatio();

    // Validate aspect ratio - ensure it's reasonable (between 0.1 and 10)
    // This prevents extreme aspect ratios that cause display issues.
    // Keep the last known-good ratio to avoid flicker when partial cache updates
    // temporarily omit width/height/aspect_ratio.
    const validAspectRatio = aspectRatio && aspectRatio > 0.1 && aspectRatio < 10 ? aspectRatio : null;
    const stableAspectRatioRef = useRef(validAspectRatio || null);
    if (validAspectRatio) {
        stableAspectRatioRef.current = validAspectRatio;
    }
    const effectiveAspectRatio = validAspectRatio || stableAspectRatioRef.current || null;
    const effectiveMedia = useMemo(() => {
        const target = image || {};
        if (effectiveAspectRatio && !target.aspect_ratio) {
            return { ...target, aspect_ratio: effectiveAspectRatio };
        }
        return target;
    }, [image, effectiveAspectRatio]);

    const {
        width: mediaFrameWidth,
        height: mediaFrameHeight,
        resolvedImageWidth: fallbackFrameWidth
    } = calculateImageFrameBounds(effectiveMedia, width, isCompactViewport);

    // Detect if this is a Cloudinary image (has public_id)
    // Cloudinary images have a public_id like "luckynote/abc123" - S3 images don't have one
    // Cloudinary images should NOT use cdn_url - they should use Cloudinary URLs directly
    const isCloudinaryImage = isCloudinaryMedia(image);

    // Helper function to select appropriate thumbnail size based on display width
    const getThumbnailUrl = (image, displayWidth) => {
        // If URL is a blob URL (preview from local file), use it directly
        if (image?.url && (image.url.startsWith('blob:') || image.url.startsWith('data:'))) {
            return image.url;
        }
        
        // For Cloudinary images, don't use cdn_url or thumbnails - use Cloudinary URL directly
        if (isCloudinaryImage) {
            return image?.url || url;
        }
        
        if (!image?.thumbnail) {
            // Fallback to cdn_url or url if thumbnails not available (S3 images only)
            return image?.cdn_url || image?.url || url;
        }

        // Select thumbnail size based on display width (S3 images only)
        // For list views (MessagesList), use at least md (512px) for better quality
        // sm: 256px for very small thumbnails (< 200px)
        // md: 512px for list views and medium previews (200-800px) - minimum for lists
        // lg: 1080px for large views (800-1600px)
        // xl: 1600px for very large displays (> 1600px)
        // original: for full quality when needed (downloads only)
        if (displayWidth <= 200) {
            return image.thumbnail.sm || image.thumbnail.md || image.thumbnail.lg || image.thumbnail.original;
        } else if (displayWidth <= 800) {
            // List views: use md (512px) minimum for better quality
            return image.thumbnail.md || image.thumbnail.lg || image.thumbnail.original;
        } else if (displayWidth <= 1600) {
            return image.thumbnail.lg || image.thumbnail.xl || image.thumbnail.original;
        } else {
            return image.thumbnail.xl || image.thumbnail.original;
        }
    };

    // Prefer thumbnail (appropriate size) if available, then cdn_url, then url
    // For Cloudinary images: use url directly (no cdn_url, no thumbnails)
    const initialUrl = getThumbnailUrl(image, imageWidth);
    const failedUrlsRef = useRef(new Set());
    const sourceSignatureRef = useRef('');

    const thumbnailSignature = useMemo(
        () =>
            [
                toStableUrlKey(image?.thumbnail?.sm || ''),
                toStableUrlKey(image?.thumbnail?.md || ''),
                toStableUrlKey(image?.thumbnail?.lg || ''),
                toStableUrlKey(image?.thumbnail?.xl || ''),
                toStableUrlKey(image?.thumbnail?.original || '')
            ].join('|'),
        [lnT,
            image?.thumbnail?.sm,
            image?.thumbnail?.md,
            image?.thumbnail?.lg,
            image?.thumbnail?.xl,
            image?.thumbnail?.original
        ]
    );

    const preferredImageUrl = useMemo(
        () => getThumbnailUrl(image, imageWidth),
        [lnT, image?.url, image?.cdn_url, image?.public_id, thumbnailSignature, imageWidth, url]
    );

    const sourceSignature = useMemo(
        () =>
            [
                messageId || '',
                image?.public_id || '',
                toStableUrlKey(image?.url || ''),
                toStableUrlKey(image?.cdn_url || ''),
                thumbnailSignature,
                imageWidth,
                toStableUrlKey(url || '')
            ].join('|'),
        [lnT, messageId, image?.public_id, image?.url, image?.cdn_url, thumbnailSignature, imageWidth, url]
    );

    const [privateImageUrl, setImageUrl] = useState(() => {
        const cached = readCachedImageUrl(sourceSignature);
        return cached || initialUrl;
    });
    const publicImage = useImageRecovery({
        sources: readOnly ? [preferredImageUrl, image?.cdn_url, image?.url, url] : [],
        allowRefresh: false
    });
    const imageUrl = readOnly ? publicImage.src : privateImageUrl;
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hasTriedRefetch, setHasTriedRefetch] = useState(false);

    const updateImageUrl = useCallback(
        nextUrl => {
            const normalized = String(nextUrl || '').trim();
            if (!normalized) return;
            rememberCachedImageUrl(sourceSignature, normalized);
            setImageUrl(prevUrl => (prevUrl === normalized ? prevUrl : normalized));
        },
        [lnT, sourceSignature]
    );

    const pickFirstHealthyUrl = useCallback((candidates = []) => {
        for (const candidate of candidates) {
            const normalized = String(candidate || '').trim();
            if (!normalized) continue;
            if (!failedUrlsRef.current.has(normalized)) {
                return normalized;
            }
        }
        return null;
    }, [lnT]);

    const buildFallbackUrl = useCallback(
        currentUrl => {
            const candidates = [];
            const current = String(currentUrl || '').trim();

            if (!isCloudinaryImage && image?.thumbnail) {
                if (current === image.thumbnail.sm) {
                    candidates.push(image.thumbnail.md, image.thumbnail.lg, image.thumbnail.xl, image.thumbnail.original);
                } else if (current === image.thumbnail.md) {
                    candidates.push(image.thumbnail.lg, image.thumbnail.xl, image.thumbnail.original);
                } else if (current === image.thumbnail.lg) {
                    candidates.push(image.thumbnail.xl, image.thumbnail.original);
                } else if (current === image.thumbnail.xl) {
                    candidates.push(image.thumbnail.original);
                } else {
                    candidates.push(image.thumbnail.md, image.thumbnail.lg, image.thumbnail.xl, image.thumbnail.original);
                }
            }

            if (!isCloudinaryImage && image?.cdn_url && current !== image.cdn_url) {
                candidates.push(image.cdn_url);
            }

            if (image?.url && current !== image.url) {
                candidates.push(image.url);
            }

            return pickFirstHealthyUrl(candidates);
        },
        [lnT,
            isCloudinaryImage,
            image?.thumbnail?.sm,
            image?.thumbnail?.md,
            image?.thumbnail?.lg,
            image?.thumbnail?.xl,
            image?.thumbnail?.original,
            image?.cdn_url,
            image?.url,
            pickFirstHealthyUrl
        ]
    );
    
    // Query to refetch message (regenerates CDN URL if expired)
    const [refetchMessage] = useLazyQuery(GET_MESSAGE, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const refreshedImage = data?.getMessage?.image;
            if (refreshedImage?.cdn_url) {
                console.log(`[ImageSection] ✅ Refetched message - new CDN URL received`);
                // Update to use new CDN URL
                const newThumbnailUrl = getThumbnailUrl(refreshedImage, imageWidth);
                if (newThumbnailUrl) {
                    failedUrlsRef.current.clear();
                    updateImageUrl(newThumbnailUrl);
                }
            }
            setIsRefreshing(false);
        },
        onError: (error) => {
            console.error('[ImageSection] Failed to refetch message:', error);
            setIsRefreshing(false);
        }
    });
    
    const hasTriedS3Refresh = useRef(false);
    const [refreshUrl] = useLazyQuery(REFRESH_MESSAGE_MEDIA_URLS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.refreshMessageMediaUrls?.image?.url) {
                // CDN has already failed when this fallback query runs.
                if (!isCloudinaryImage) {
                    updateImageUrl(data.refreshMessageMediaUrls.image.url);
                } else {
                    updateImageUrl(image?.url || data.refreshMessageMediaUrls.image.url);
                }
                setIsRefreshing(false);
            }
        },
        onError: (error) => {
            console.error('Failed to refresh S3 signed URL:', error);
            setIsRefreshing(false);
            // Fallback to original url if refresh fails and we don't have cdn_url
            if (image?.url && !image?.cdn_url) {
                updateImageUrl(image.url);
            }
        }
    });

    // Check if CDN URL is expired before using it
    const checkCdnUrlExpiration = (cdnUrl) => {
        if (!cdnUrl) return false;
        const expiresMatch = cdnUrl.match(/[?&]expires=(\d+)/);
        if (expiresMatch) {
            const expiresTimestamp = parseInt(expiresMatch[1], 10) * 1000; // Convert to milliseconds
            const isExpired = expiresTimestamp < Date.now();
            if (isExpired) {
                const expiredBy = Math.round((Date.now() - expiresTimestamp) / 1000);
                console.warn(`[ImageSection] ⚠️ CDN URL expired (expired ${expiredBy}s ago) - will refetch message`);
                return true;
            }
        }
        return false;
    };

    // Update imageUrl when image prop changes
    // Prefer thumbnails (appropriate size) > cdn_url > url (for S3 images)
    // For Cloudinary images: use url directly
    // Also handle blob URLs (preview URLs from local files)
    React.useEffect(() => {
        const sourceChanged = sourceSignatureRef.current !== sourceSignature;
        if (sourceChanged) {
            sourceSignatureRef.current = sourceSignature;
            hasTriedS3Refresh.current = false;
            failedUrlsRef.current.clear();
            const cachedUrl = readCachedImageUrl(sourceSignature);
            if (cachedUrl && cachedUrl !== imageUrl) {
                setImageUrl(cachedUrl);
                return;
            }
        }
        
        // Check if CDN URL is expired (only for S3 images with cdn_url)
        if (preferredImageUrl && !isCloudinaryImage && image?.cdn_url && preferredImageUrl === image.cdn_url) {
            const isExpired = checkCdnUrlExpiration(image.cdn_url);
            if (isExpired && messageId) {
                // CDN URL expired - refetch message to get regenerated URL
                console.log(`[ImageSection] 🔄 CDN URL expired, refetching message ${messageId}...`);
                // Note: We'll handle refetch in onError handler, but log here for visibility
            }
        }

        if (!preferredImageUrl) {
            return;
        }

        // Keep the currently rendered fallback URL if the preferred URL already failed.
        if (!sourceChanged && failedUrlsRef.current.has(preferredImageUrl)) {
            return;
        }

        const preferredUrlKey = toStableUrlKey(preferredImageUrl);
        const currentUrlKey = toStableUrlKey(imageUrl);
        const currentImageFailed = failedUrlsRef.current.has(String(imageUrl || '').trim());
        const pathChanged = preferredUrlKey && currentUrlKey && preferredUrlKey !== currentUrlKey;
        const shouldRetryWithNewSignedVariant = currentImageFailed && preferredImageUrl !== imageUrl;

        // Ignore token/query-only URL churn to prevent visual source swapping flicker.
        if ((pathChanged || shouldRetryWithNewSignedVariant) && preferredImageUrl !== imageUrl) {
            updateImageUrl(preferredImageUrl);
        }
    }, [preferredImageUrl, sourceSignature, isCloudinaryImage, image?.cdn_url, messageId, imageUrl, updateImageUrl]);

    const handleImageLoad = useCallback(
        event => {
            const loadedUrl = String(
                event?.currentTarget?.currentSrc ||
                    event?.currentTarget?.src ||
                    imageUrl ||
                    ''
            ).trim();
            if (!loadedUrl) return;
            rememberCachedImageUrl(sourceSignature, loadedUrl);
            setHasTriedRefetch(false);
            setIsRefreshing(false);
        },
        [lnT, imageUrl, sourceSignature]
    );

    const handleImageError = useCallback(async () => {
        if (readOnly) { await publicImage.onError(); return; }
        const currentUrl = String(imageUrl || '').trim();
        if (currentUrl) {
            failedUrlsRef.current.add(currentUrl);
        }

        if (!messageId || isRefreshing) {
            console.warn('Image failed to load:', currentUrl);
            return;
        }

        setIsRefreshing(true);

        // Bunny CDN URLs all share the same token — if one fails, the others will too.
        // Skip the thumbnail fallback chain and refetch immediately to get a fresh token.
        const isBunnyUrl = currentUrl.includes('.b-cdn.net') || currentUrl.includes('bunny.net');
        if (isBunnyUrl && !hasTriedRefetch && messageId) {
            console.warn('Image failed to load, refreshing Bunny CDN URL:', currentUrl);
            setHasTriedRefetch(true);
            refetchMessage({ variables: { data: { id: messageId } } });
            return;
        }

        console.warn('Image failed to load, trying fallback:', currentUrl);

        let fallbackUrl = buildFallbackUrl(currentUrl);

        if (!fallbackUrl) {
            const isS3SourceUrl =
                String(image?.url || '').includes('amazonaws.com') ||
                String(image?.url || '').includes('s3.amazonaws.com');

            if (!isCloudinaryImage && isS3SourceUrl && messageId && !hasTriedS3Refresh.current) {
                hasTriedS3Refresh.current = true;
                try {
                    await refreshUrl({
                        variables: {
                            data: { messageId }
                        }
                    });
                    setIsRefreshing(false);
                    return;
                } catch (error) {
                    console.error('Failed to refresh S3 URL:', error);
                }
            }
        }

        if (fallbackUrl && fallbackUrl !== currentUrl) {
            updateImageUrl(fallbackUrl);
        } else {
            console.warn('No healthy fallback URL available');
        }

        setIsRefreshing(false);
    }, [readOnly, publicImage.onError, lnT, imageUrl, messageId, isRefreshing, hasTriedRefetch, buildFallbackUrl, image?.url, isCloudinaryImage, refreshUrl, updateImageUrl, refetchMessage]);

    useEffect(() => {
        if (!showActionMenu) {
            return undefined;
        }

        const handlePointerDown = event => {
            const target = event.target;
            if (actionMenuRef.current?.contains(target) || actionButtonRef.current?.contains(target)) {
                return;
            }
            setShowActionMenu(false);
        };

        const handleEscape = event => {
            if (event.key === 'Escape') {
                setShowActionMenu(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleEscape, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape, true);
        };
    }, [showActionMenu]);

    useEffect(() => {
        if (!showActionMenu) {
            return;
        }

        const updateActionMenuPosition = () => {
            const rect = actionButtonRef.current?.getBoundingClientRect();
            if (!rect) {
                return;
            }

            const menuWidth = 154;
            const horizontalPadding = 8;
            const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
            const left = Math.max(
                horizontalPadding,
                Math.min(rect.right - menuWidth, viewportWidth - menuWidth - horizontalPadding)
            );

            setActionMenuPosition({
                top: Math.round(rect.bottom + 6),
                left: Math.round(left)
            });
        };

        updateActionMenuPosition();
        window.addEventListener('scroll', updateActionMenuPosition, true);
        window.addEventListener('resize', updateActionMenuPosition);

        return () => {
            window.removeEventListener('scroll', updateActionMenuPosition, true);
            window.removeEventListener('resize', updateActionMenuPosition);
        };
    }, [showActionMenu]);

    const resolveFreshOpenUrl = async () => {
        if (readOnly || !image?.name) {
            return image?.thumbnail?.original || image?.cdn_url || image?.url || url;
        }

        try {
            const { data, error } = await refetchDownloadFile({
                data: {
                    fileName: image.name
                }
            });

            if (error) {
                throw error;
            }

            if (data?.downloadFile?.__typename === 'S3File' && (data.downloadFile.cdn_url || data.downloadFile.url)) {
                return data.downloadFile.cdn_url || data.downloadFile.url;
            }
        } catch (error) {
            console.error('Failed to refresh image URL before opening:', error);
        }

        return image?.thumbnail?.original || image?.cdn_url || image?.url || url;
    };

    const openImage = async event => {
        event?.stopPropagation?.();
        const targetUrl = await resolveFreshOpenUrl();
        if (!targetUrl) {
            return;
        }

        if (isTauriEnvironment()) {
            openExternalUrl(targetUrl);
        } else {
            const openedWindow = window.open('about:blank', '_blank');
            if (openedWindow) {
                openedWindow.opener = null;
                openedWindow.location.replace(targetUrl);
            } else {
                window.open(targetUrl, '_blank', 'noopener,noreferrer');
            }
        }

        setShowActionMenu(false);
    };

    const downloadCurrentImage = async event => {
        event?.stopPropagation?.();
        if (readOnly) {
            try {
                await downloadReadOnlyAttachment(image || { url, cdn_url: url }, true);
            } catch (err) {
                console.error('Failed to download read-only image:', err);
            }
        } else {
            const imageName = image?.name || (typeof url === 'string' ? url.substr(url.lastIndexOf('/') + 1) : 'image.png');
            downloadFile(imageName, image?.size, image?.displayName);
        }
        setShowActionMenu(false);
    };

    if (imageUrl) {
        // Detect URL type
        const isBlobUrl = imageUrl.startsWith('blob:') || imageUrl.startsWith('data:');
        const isBunnyUrl = imageUrl.includes('.b-cdn.net') || imageUrl.includes('bunny.net');
        const isS3Url = imageUrl.includes('amazonaws.com') || imageUrl.includes('s3.amazonaws.com');
        const isCloudinaryUrl = isCloudinaryMediaUrl(imageUrl);
        const isDirectImageUrl = !image?.public_id && imageUrl.match(/\.(jpeg|jpg|gif|png)$/i);
        
        // Use regular img tag for blob URLs (previews), Bunny URLs, S3 URLs, or direct image URLs
        const shouldUseImgTag = isBlobUrl || isBunnyUrl || isS3Url || isDirectImageUrl;
        
        const coverStyle = cover ? {
            position: 'relative',
            width: '100%',
            aspectRatio: effectiveAspectRatio ? `1 / ${effectiveAspectRatio}` : '4 / 3',
            backgroundColor: 'transparent',
            borderRadius: 8,
            overflow: 'hidden',
        } : {
            position: 'relative',
            borderRadius: 8,
            backgroundColor: NOTE_BACKGROUND,
            width: '100%',
            maxWidth: effectiveAspectRatio ? mediaFrameWidth : fallbackFrameWidth,
            minWidth: effectiveAspectRatio ? Math.min(mediaFrameWidth, LAYOUT.MIN_MEDIA_WIDTH) : LAYOUT.MIN_MEDIA_WIDTH,
            ...(effectiveAspectRatio ? {
                height: mediaFrameHeight,
                overflow: 'hidden'
            } : {
                minHeight: 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            })
        };

        const canShowActions = Boolean(imageUrl && !isUploading);

        return (
            <ImageFrame
                data-message-media-interactive="true"
                style={coverStyle}
                onClick={e => {
                    if (onClick) {
                        e.stopPropagation();
                        onClick();
                    }
                }}
            >
                {shouldUseImgTag ? (
                    // Regular img tag for Bunny URLs, S3 URLs, or direct image URLs
                    <img
                        src={imageUrl}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: cover ? 'cover' : 'contain',
                            objectPosition: 'center',
                            borderRadius: cover ? 0 : 8,
                            opacity: isUploading ? 0.5 : 1, // Pale/faded when uploading
                            transition: 'opacity 0.3s ease-in-out', // Smooth transition when upload completes
                            display: 'block'
                        }}
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                        {...rest}
                    />
                ) : image?.format === 'heic' ? (
                    <Image
                        cloudName={environment.REACT_APP_CLOUD_NAME}
                        publicId={image.public_id}
                        width={width ? width : '300'}
                        crop={crop ? crop : 'fit'}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: cover ? 'cover' : 'contain',
                            objectPosition: 'center',
                            borderRadius: cover ? 0 : 8,
                            opacity: isUploading ? 0.5 : 1, // Pale/faded when uploading
                            transition: 'opacity 0.3s ease-in-out', // Smooth transition when upload completes
                            display: 'block'
                        }}
                        onLoad={handleImageLoad}
                        {...rest}
                    >
                        <Transformation fetchFormat="auto" flags="lossy" quality="50" />
                    </Image>
                ) : isCloudinaryUrl ? (
                    <Image
                        cloudName={environment.REACT_APP_CLOUD_NAME}
                        publicId={imageUrl}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: cover ? 'cover' : 'contain',
                            objectPosition: 'center',
                            borderRadius: cover ? 0 : 8,
                            opacity: isUploading ? 0.5 : 1, // Pale/faded when uploading
                            transition: 'opacity 0.3s ease-in-out', // Smooth transition when upload completes
                            display: 'block'
                        }}
                        onLoad={handleImageLoad}
                        {...rest}
                    >
                        <Transformation fetchFormat="auto" flags="lossy" quality="50" />
                    </Image>
                ) : (
                    // Fallback to regular img tag for unknown URL types
                    <img
                        src={imageUrl}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: cover ? 'cover' : 'contain',
                            objectPosition: 'center',
                            borderRadius: cover ? 0 : 8,
                            opacity: isUploading ? 0.5 : 1, // Pale/faded when uploading
                            transition: 'opacity 0.3s ease-in-out', // Smooth transition when upload completes
                            display: 'block'
                        }}
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                        {...rest}
                    />
                )}

                {/* Upload progress bar overlay */}
                {isUploading && (
                    <div style={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 4,
                        padding: '4px 8px'
                    }}>
                        <div style={{
                            flex: 1,
                            height: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                height: '100%',
                                width: `${Math.min(uploadProgress, 100)}%`,
                                backgroundColor: uploadStatus === 'error' ? '#ff4444' : PRIMARY_COLOR_8,
                                borderRadius: 2,
                                transition: 'width 0.15s ease-out'
                            }} />
                        </div>
                        <span style={{ fontSize: 11, color: SECONDARY_COLOR, minWidth: 40, textAlign: 'right' }}>
                            {uploadStatus === 'error' ? lnT('messages:failed') :
                             uploadStatus === 'processing' ? lnT('common:saving') :
                             Number(image?.size || 0) >= 2 * 1024 * 1024 * 1024 ? lnT('common:text', { value1: String(Number(uploadProgress || 0).toFixed(1)) }) : lnT('common:text', { value1: String(Math.round(Number(uploadProgress || 0))) })}
                        </span>
                        {typeof onCancelUpload === 'function' && uploadStatus !== 'error' ? (
                            <button
                                type="button"
                                title={lnT('messages:cancelUpload')}
                                aria-label={lnT('messages:cancelUpload')}
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onCancelUpload();
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: SECONDARY_COLOR,
                                    borderRadius: 4,
                                    opacity: 0.8,
                                    transition: 'opacity 0.15s ease, color 0.15s ease'
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.opacity = '1';
                                    e.currentTarget.style.color = '#ff4444';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.opacity = '0.8';
                                    e.currentTarget.style.color = SECONDARY_COLOR;
                                }}
                            >
                                <FiX size={12} />
                            </button>
                        ) : null}
                    </div>
                )}
                {canShowActions ? (
                    <ImageActionsAnchor>
                        <ImageActionsButton
                            ref={actionButtonRef}
                            type="button"
                            aria-label={lnT('messages:imageActions', { defaultValue: 'Image actions' })}
                            aria-expanded={showActionMenu}
                            data-message-interactive="true"
                            $isMenuOpen={showActionMenu}
                            onClick={event => {
                                event.stopPropagation();
                                setShowActionMenu(previous => !previous);
                            }}
                        >
                            <FiMoreVertical size={16} />
                        </ImageActionsButton>
                        {showActionMenu && typeof document !== 'undefined'
                            ? ReactDOM.createPortal(
                                  <ImageActionMenu
                                      ref={actionMenuRef}
                                      style={{
                                          top: `${actionMenuPosition.top}px`,
                                          left: `${actionMenuPosition.left}px`
                                      }}
                                      onClick={event => event.stopPropagation()}
                                  >
                                      <ImageActionMenuItem type="button" onClick={downloadCurrentImage}>
                                          <FiDownload size={14} />
                                          <span>{lnT('messages:download')}</span>
                                      </ImageActionMenuItem>
                                  </ImageActionMenu>,
                                  document.body
                              )
                            : null}
                    </ImageActionsAnchor>
                ) : null}
            </ImageFrame>
        );
    }
    // return null;
}

const ImageFrame = styled.div`
    position: relative;
    user-select: none;
`;

const ImageActionsAnchor = styled.div`
    position: absolute;
    right: 6px;
    top: 6px;
    z-index: 3;
`;

const ImageActionsButton = styled.button`
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: ${props => props.$isMenuOpen ? 'rgba(0, 0, 0, 0.72)' : 'rgba(0, 0, 0, 0.48)'};
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: ${props => (props.$isMenuOpen ? 1 : 0)};
    transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease;

    ${ImageFrame}:hover & {
        opacity: 1;
    }

    &:focus-visible {
        opacity: 1;
        outline: 2px solid rgba(255, 255, 255, 0.72);
        outline-offset: 2px;
    }

    @media (hover: none) {
        opacity: 0.85;
    }

    &:hover {
        background: rgba(0, 0, 0, 0.78);
        color: #ffffff;
    }
`;

const ImageActionMenu = styled.div`
    position: fixed;
    min-width: 154px;
    padding: 4px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(20, 24, 28, 0.96);
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.26);
    backdrop-filter: saturate(180%) blur(18px);
    -webkit-backdrop-filter: saturate(180%) blur(18px);
    z-index: 10010;
`;

const ImageActionMenuItem = styled.button`
    width: 100%;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: rgba(255, 255, 255, 0.86);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 9px;
    font: inherit;
    font-size: 13px;
    line-height: 1.2;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: ${WHITE};
    }
`;

export default ImageSection;
